import { useEffect, useRef } from 'react';

import type { EmergencyIncident, EmergencyTimelineEntry } from '@src/models/interfaces/emergency.interface';
import { EmergencyTimelineService } from '@src/services/EmergencyTimelineService';

import { EMERGENCY_TIMELINE_TYPES } from '../constants';

interface Options {
  incidents: EmergencyIncident[];
  organizationId: string;
  enabled: boolean;
  resetKey: string;
  muted: boolean;
  notificationPermission: NotificationPermission | 'unsupported';
  messages: {
    title: string;
    titleFor?: (incident: EmergencyIncident) => string;
    body: (incident: EmergencyIncident, entry: EmergencyTimelineEntry) => string;
  };
  onOpenMessages: (incidentId: string, messageId: string) => void;
  onIncomingMessage: (incident: EmergencyIncident, entry: EmergencyTimelineEntry) => void;
}

/** Watches active incident timelines for incoming notes, silently baselining each stream. */
export const useEmergencyMessageAlerts = ({
  incidents,
  organizationId,
  enabled,
  resetKey,
  muted,
  notificationPermission,
  messages,
  onOpenMessages,
  onIncomingMessage,
}: Options): void => {
  const callbacksRef = useRef({
    muted,
    notificationPermission,
    messages,
    onOpenMessages,
    onIncomingMessage,
  });
  callbacksRef.current = {
    muted,
    notificationPermission,
    messages,
    onOpenMessages,
    onIncomingMessage,
  };
  const serviceRef = useRef<EmergencyTimelineService | null>(null);
  const baselinesRef = useRef(new Map<string, Set<string>>());
  if (!serviceRef.current) serviceRef.current = new EmergencyTimelineService();

  useEffect(() => {
    if (!enabled || !organizationId) return undefined;
    let disposed = false;
    const subscriptions = new Map<string, () => void>();
    const active = new Map(incidents.map(incident => [incident.id, incident]));
    const notificationKey = `${organizationId}:${resetKey}`;

    incidents.forEach(incident => {
      const unsubscribe = serviceRef.current!.subscribe(
        organizationId,
        incident.id,
        entries => {
          if (disposed) return;
          const current = entries.filter(entry => entry.type === EMERGENCY_TIMELINE_TYPES.NOTE_ADDED);
          const currentIds = new Set(current.map(entry => entry.id));
          const baselineKey = `emergency-message-baseline:${notificationKey}:${incident.id}`;
          const priorIds = baselinesRef.current.get(baselineKey);
          baselinesRef.current.set(baselineKey, currentIds);
          if (!priorIds) return;
          const arrived = current.filter(entry => !priorIds.has(entry.id));
          // actorId identifies the account, not the device. The same operator
          // can send a note from the native app while this PWA is open, so do
          // not treat every message by the current account as locally sent.
          if (!arrived.length) return;

          if (!callbacksRef.current.muted) playMessageTone();
          arrived.forEach(entry => {
            const currentIncident = active.get(incident.id);
            if (!currentIncident) return;
            callbacksRef.current.onIncomingMessage(currentIncident, entry);
            if (callbacksRef.current.notificationPermission !== 'granted') return;
            try {
              const notification = new Notification(
                callbacksRef.current.messages.titleFor?.(currentIncident) ??
                  callbacksRef.current.messages.title,
                {
                  body: callbacksRef.current.messages.body(currentIncident, entry),
                  // One system notification per incident; newer messages replace it.
                  tag: `emergency-message-${incident.id}`,
                  requireInteraction: true,
                },
              );
              notification.onclick = () => {
                window.focus();
                callbacksRef.current.onOpenMessages(incident.id, entry.id);
                notification.close();
              };
            } catch {
              // Some browsers expose Notification but only allow construction from a service worker.
            }
          });
        },
        () => undefined,
      );
      subscriptions.set(incident.id, unsubscribe);
    });

    return () => {
      disposed = true;
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [enabled, incidents, organizationId, resetKey]);
};

const playMessageTone = (): void => {
  if (typeof window === 'undefined') return;
  const AudioContextConstructor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;

  try {
    const context = new AudioContextConstructor();
    const play = () => {
      const now = context.currentTime;
      // Two clear rising tones, louder than a plain blip so it is noticed.
      [660, 880].forEach((frequency, index) => {
        const startAt = now + index * 0.18;
        const endAt = startAt + 0.16;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(frequency, startAt);
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(0.35, startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, endAt);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startAt);
        oscillator.stop(endAt + 0.02);
      });
      window.setTimeout(() => void context.close(), 800);
    };
    if (context.state === 'suspended') void context.resume().then(play).catch(() => void context.close());
    else play();
  } catch {
    // Audio is an enhancement; browser restrictions must not block messages.
  }
};
